import { FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useEffect, useState } from "react";
import { forceLogout } from "../../../store/allocation/allocationAuthSlice";
import EmployeeAuthModal from "../../auth/employeeAuthModal";
import { usePortalReviewAccess } from "../../../hooks/usePortalReviewAccess";
import ReviewSubmitModal from "../../portal-reviews/ReviewSubmitModal";
import { fetchPendingReviewCount } from "../../../store/allocation/portalReviewSlice";
import api from "../../../utils/api";
import { getSocket } from "../../../socket";

const TODAY_WORK_ROLES = new Set([
  "designer",
  "senior designer",
  "assistant captain",
  "captain",
  "super admin",
]);

const TopBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const [search, setSearch] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);

  const isOnEmployeeDashboard = location.pathname.startsWith("/dashboard/employee");
  const isOnAllocation = location.pathname.startsWith("/dashboard/allocation");
  const isOnUnits =
    location.pathname === "/dashboard/admin/units" ||
    location.pathname.startsWith("/dashboard/admin/units/") ||
    location.pathname === "/dashboard/admin/marked-units";

  const { canSubmit, canViewInbox, canSeeReviewButton } = usePortalReviewAccess();
  const { pendingCount } = useAppSelector((state) => state.portalReviews);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const [todayWork, setTodayWork] = useState<{ cases: number; units: number } | null>(null);

  const canSeeTodayWork = TODAY_WORK_ROLES.has(
    String(employee?.role || "").trim().toLowerCase(),
  );

  const canSeeLeaveAlerts =
    user?.role === "Super Admin" ||
    ["admin", "executive", "super admin"].includes(
      String(employee?.role || "").trim().toLowerCase(),
    );

  useEffect(() => {
    if (!canViewInbox) return;
    dispatch(fetchPendingReviewCount());
  }, [canViewInbox, dispatch]);

  useEffect(() => {
    if (!canSeeLeaveAlerts) {
      setPendingLeaveCount(0);
      return;
    }
    let active = true;
    const load = () => {
      api
        .get("/allocation-tool/employee/leave/pending-count")
        .then((res) => {
          if (active) setPendingLeaveCount(Number(res.data?.data?.count) || 0);
        })
        .catch(() => {
          if (active) setPendingLeaveCount(0);
        });
    };
    load();

    const socket = getSocket();
    socket.emit("join_leave_alerts_room");
    socket.on("leave_pending_changed", load);

    return () => {
      active = false;
      socket.off("leave_pending_changed", load);
      socket.emit("leave_leave_alerts_room");
    };
  }, [canSeeLeaveAlerts, location.pathname]);

  useEffect(() => {
    if (!employee || !canSeeTodayWork) {
      setTodayWork(null);
      return;
    }
    let active = true;
    const load = () => {
      api
        .get("/hr/me/shift-work")
        .then((res) => {
          const data = res.data?.data;
          if (!active) return;
          if (data?.show && data?.punchedIn) {
            setTodayWork({
              cases: Number(data.cases) || 0,
              units: Number(data.units) || 0,
            });
          } else {
            setTodayWork(null);
          }
        })
        .catch(() => {
          if (active) setTodayWork(null);
        });
    };
    load();
    const timer = window.setInterval(load, 60000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [employee?.emp_id, employee?.role, canSeeTodayWork]);

  const canOpenEmployeeDashboard =
    user?.role === "Production" ||
    user?.role === "Operations" ||
    user?.role === "Super Admin";

  const handleClick = () => {
    if (
      (user?.role.toLowerCase() !== "user" && user?.role.toLowerCase() !== "side admin") &&
      (isOnAllocation || isOnUnits)
    ) {
      navigate("/dashboard/admin/cases");
    } else {
      navigate("/dashboard/allocation/unallocated");
    }
  };

  const handleEmployeeDashboardClick = () => {
    if (isOnEmployeeDashboard) {
      navigate("/dashboard/admin/cases");
      return;
    }
    if (canSeeLeaveAlerts && pendingLeaveCount > 0) {
      navigate("/dashboard/employee/leaves");
      return;
    }
    navigate("/dashboard/employee");
  };

  const handleReviewClick = () => {
    if (canSubmit) {
      setShowReviewModal(true);
      return;
    }
    if (canViewInbox) {
      navigate("/dashboard/employee/reviews");
    }
  };

  const handleReviewInboxClick = () => {
    navigate("/dashboard/employee/reviews");
  };

  const handleLogout = () => {
    forceLogout(dispatch);
    // Production portal: after any employee logs out, land on allocation board
    // so the next designer can sign in there (no extra login-page link needed).
    if (user?.role === "Production") {
      navigate("/dashboard/allocation/unallocated");
    }
  };

  const handleSearch = () => {
    if (!search.trim()) return;
    navigate(`/dashboard/admin/cases/${search}`);
  };

  return (
    <>
    <div className="bg-[#111111] hidden text-sm text-white px-[4%] py-2 md:flex justify-between items-center">
      <div className="w-full flex gap-6 items-center justify-between text-sm">

        <div className="flex items-center gap-2">
          {employee ? (
            <p className="text-red-500 font-bold mr-4">
              Login user : {employee?.emp_name}
            </p>
          ) : null}
          {user &&
            user.role.toLowerCase() !== "user" &&
            user.role.toLowerCase() !== "admin read" &&
            user.role.toLowerCase() !== "admin" &&
            user.role.toLowerCase() !== "side admin" ? (
            <div className="flex gap-8 justify-between">
              <div className="flex gap-4">
                {/* {user.role.toLowerCase() === "super admin" ? (
                  <button
                    onClick={handleSmileClick}
                    className="px-4 py-1 bg-[#2fa9bf] text-white rounded-xl"
                  >
                    {location.pathname.startsWith("/dashboard/smiledesign")
                      ? "Cases Portal"
                      : "Smile Design Portal"}
                  </button>
                ) : null} */}
                {user.role.toLowerCase() !== "user" ||
                  user.role.toLowerCase() !== "side admin" ? (
                  <button
                    onClick={handleClick}
                    className="px-4 py-1 bg-[#1E1E1E] text-white rounded-xl"
                  >
                    {isOnAllocation || isOnUnits
                      ? "Cases Portal"
                      : "Allocation Portal"}
                  </button>
                ) : null}
                {canOpenEmployeeDashboard ? (
                  <button
                    onClick={handleEmployeeDashboardClick}
                    className="relative px-4 py-1 bg-[#1E1E1E] text-white rounded-xl"
                  >
                    {isOnEmployeeDashboard
                      ? "Cases Portal"
                      : "Employee Dashboard"}
                    {!isOnEmployeeDashboard &&
                    canSeeLeaveAlerts &&
                    pendingLeaveCount > 0 ? (
                      <span className="absolute -top-1.5 -right-1.5 inline-flex min-w-[1.15rem] h-[1.15rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
                        {pendingLeaveCount > 99 ? "99+" : pendingLeaveCount}
                      </span>
                    ) : null}
                  </button>
                ) : null}
              </div>

              {user?.role?.toLowerCase() !== "super admin" &&
                (employee ? (
                  <button
                    onClick={handleLogout}
                    className="px-4 py-1 bg-red-500 text-white rounded-lg"
                  >
                    Logout
                  </button>
                ) : (
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="px-4 py-1 bg-green-500 text-white rounded-lg"
                  >
                    Login
                  </button>
                ))}

              <li className="flex items-center gap-2 text-sm font-normal">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="border px-2 py-1 text-black bg-gray-100 rounded uppercase"
                />
                <button
                  className="bg-blue-400 px-2 py-1 rounded-md  text-white"
                  onClick={handleSearch}
                >
                  Search
                </button>
              </li>
            </div>
          ) : null}
          {canSeeReviewButton ? (
            <div className="flex items-center gap-2 ml-2">
              <button
                onClick={handleReviewClick}
                className="px-4 py-1 bg-[#2fa9bf] text-white rounded-xl"
              >
                Feedback
              </button>
              {canViewInbox ? (
                <button
                  onClick={handleReviewInboxClick}
                  className="relative px-4 py-1 bg-[#1E1E1E] text-white rounded-xl"
                >
                  Feedback inbox
                  {pendingCount > 0 ? (
                    <span className="absolute -top-1.5 -right-1.5 inline-flex min-w-[1.15rem] h-[1.15rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
                      {pendingCount > 99 ? "99+" : pendingCount}
                    </span>
                  ) : null}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          {todayWork ? (
            <p
              className="whitespace-nowrap rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs text-white"
              title="Cases since punch-in today. Units are the job card amount, or the system calculation when the card was not edited."
            >
              <span className="text-[#9AD7E4]">Today</span>
              <span className="mx-2 text-white/30">|</span>
              <span className="font-semibold tabular-nums">{todayWork.cases}</span> cases
              <span className="mx-2 text-white/30">|</span>
              <span className="font-semibold tabular-nums">{todayWork.units}</span> units
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-2 text-[#888888]">
          <FaEnvelope className="text-blue-500" />
          <a href="mailto:support@toothsketch.com" className="hover:underline text-[#1DA8C6]">
            support@toothsketch.com
          </a>
          <FaPhoneAlt className="text-blue-500" />
          <a href="tel:+447882347988" className="hover:underline">
            +44 788 234 7988
          </a>
          <span>,</span>
          <a href="tel:+17017381847" className="hover:underline">
            +1 701 738 1847
          </a>
          {/* <a href="tel:+16467626241" className="hover:underline">
            +1 646 762 6241
          </a> */}
        </div>

      </div>
    </div>
      {showAuthModal && (
        <EmployeeAuthModal
          onClose={() => setShowAuthModal(false)}
          hasCloseControl={true}
        />
      )}
      <ReviewSubmitModal
        open={showReviewModal}
        onClose={() => setShowReviewModal(false)}
      />
    </>
  );
};

export default TopBar;
